import { useEffect, useRef, useState } from "react";
import "./App.css";
import legalPages from "./legalContent";
import siteData from "./siteData";

const {
  archetypes,
  assets,
  contentPageByRoute,
  footerLinkGroups,
  featureColumns,
  freeAccessNotes,
  homePage,
  homePageLinks,
  knownRoutes,
  navLinks,
  notFoundPage,
  privacyNotes,
  rotatingSlides,
  seoByPath,
  site,
  testimonialCards,
  valueCards,
} = siteData;

function interleaveFeatureCards(columns) {
  const cards = columns.flat();
  const imageCards = cards.filter((card) => Boolean(card.image));
  const textCards = cards.filter((card) => !card.image);
  const interleavedCards = [];
  const longestGroupLength = Math.max(imageCards.length, textCards.length);

  for (let index = 0; index < longestGroupLength; index += 1) {
    if (imageCards[index]) {
      interleavedCards.push(imageCards[index]);
    }

    if (textCards[index]) {
      interleavedCards.push(textCards[index]);
    }
  }

  return interleavedCards;
}

const stackedFeatureCards = interleaveFeatureCards(featureColumns);

function normalizePathname(pathname) {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.replace(/\/+$/, "");
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return false;
    }

    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);

    setMatches(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [query]);

  return matches;
}

function usePrefersReducedMotion() {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}

function useElementHeight(ref, fallback = 104) {
  const [height, setHeight] = useState(fallback);

  useEffect(() => {
    const node = ref.current;

    if (!node) {
      return undefined;
    }

    const updateHeight = () => {
      const nextHeight = Math.ceil(node.getBoundingClientRect().height);
      setHeight(nextHeight || fallback);
    };

    updateHeight();

    if (typeof ResizeObserver === "function") {
      const resizeObserver = new ResizeObserver(() => updateHeight());
      resizeObserver.observe(node);

      return () => resizeObserver.disconnect();
    }

    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [fallback, ref]);

  return height;
}

function usePathname() {
  const [pathname, setPathname] = useState(() => {
    if (typeof window === "undefined") {
      return "/";
    }

    return normalizePathname(window.location.pathname);
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handlePopState = () => {
      setPathname(normalizePathname(window.location.pathname));
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const handleInternalNavigation = (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const anchor = event.target.closest?.("a[href]");
      if (!anchor || anchor.target || anchor.hasAttribute("download")) {
        return;
      }

      const destination = new URL(anchor.href, window.location.href);
      if (destination.origin !== window.location.origin) {
        return;
      }

      const nextPathname = normalizePathname(destination.pathname);
      if (!knownRoutes.includes(nextPathname) || nextPathname === pathname) {
        return;
      }

      event.preventDefault();
      window.history.pushState(
        {},
        "",
        `${destination.pathname}${destination.search}${destination.hash}`,
      );
      setPathname(nextPathname);
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });

      if (destination.hash) {
        window.requestAnimationFrame(() => {
          document.querySelector(destination.hash)?.scrollIntoView();
        });
      }
    };

    document.addEventListener("click", handleInternalNavigation);
    return () =>
      document.removeEventListener("click", handleInternalNavigation);
  }, [pathname]);

  return pathname;
}

function upsertMeta({ key, attrName, attrValue, content }) {
  if (typeof document === "undefined") {
    return;
  }

  const selector = `meta[data-seo-key="${key}"], meta[${attrName}="${attrValue}"]`;
  let node = document.head.querySelector(selector);

  if (!content) {
    node?.remove();
    return;
  }

  if (!node) {
    node = document.createElement("meta");
    document.head.appendChild(node);
  }

  node.setAttribute("data-seo-key", key);
  node.setAttribute(attrName, attrValue);
  node.setAttribute("content", content);
}

function upsertCanonical(href) {
  if (typeof document === "undefined") {
    return;
  }

  let node = document.head.querySelector('link[rel="canonical"]');

  if (!href) {
    node?.remove();
    return;
  }

  if (!node) {
    node = document.createElement("link");
    node.setAttribute("rel", "canonical");
    document.head.appendChild(node);
  }

  node.setAttribute("href", href);
}

function upsertStructuredData(data) {
  if (typeof document === "undefined") {
    return;
  }

  const id = "structured-data-page";
  let node = document.getElementById(id);

  if (!data) {
    node?.remove();
    return;
  }

  if (!node) {
    node = document.createElement("script");
    node.id = id;
    node.type = "application/ld+json";
    document.head.appendChild(node);
  }

  node.textContent = JSON.stringify(data);
}

function useSeoMetadata(pathname) {
  useEffect(() => {
    const seo = seoByPath[pathname] ?? seoByPath["404"];

    if (typeof document === "undefined") {
      return;
    }

    document.title = seo.title;

    upsertMeta({
      key: "description",
      attrName: "name",
      attrValue: "description",
      content: seo.metaDescription,
    });
    upsertMeta({
      key: "robots",
      attrName: "name",
      attrValue: "robots",
      content: seo.robots,
    });
    upsertMeta({
      key: "og:title",
      attrName: "property",
      attrValue: "og:title",
      content: seo.ogTitle,
    });
    upsertMeta({
      key: "og:description",
      attrName: "property",
      attrValue: "og:description",
      content: seo.ogDescription,
    });
    upsertMeta({
      key: "og:type",
      attrName: "property",
      attrValue: "og:type",
      content: seo.ogType || "website",
    });
    upsertMeta({
      key: "og:url",
      attrName: "property",
      attrValue: "og:url",
      content:
        seo.canonicalUrl || `${site.url}${pathname === "/" ? "/" : pathname}`,
    });
    upsertMeta({
      key: "og:image",
      attrName: "property",
      attrValue: "og:image",
      content: seo.ogImage,
    });
    upsertMeta({
      key: "og:image:width",
      attrName: "property",
      attrValue: "og:image:width",
      content: String(assets.brand.ogCard.width),
    });
    upsertMeta({
      key: "og:image:height",
      attrName: "property",
      attrValue: "og:image:height",
      content: String(assets.brand.ogCard.height),
    });
    upsertMeta({
      key: "og:image:alt",
      attrName: "property",
      attrValue: "og:image:alt",
      content: seo.ogImageAlt,
    });
    upsertMeta({
      key: "twitter:card",
      attrName: "name",
      attrValue: "twitter:card",
      content: seo.twitterCard,
    });
    upsertMeta({
      key: "twitter:title",
      attrName: "name",
      attrValue: "twitter:title",
      content: seo.ogTitle,
    });
    upsertMeta({
      key: "twitter:description",
      attrName: "name",
      attrValue: "twitter:description",
      content: seo.ogDescription,
    });
    upsertMeta({
      key: "twitter:image",
      attrName: "name",
      attrValue: "twitter:image",
      content: seo.ogImage,
    });
    upsertMeta({
      key: "twitter:image:alt",
      attrName: "name",
      attrValue: "twitter:image:alt",
      content: seo.ogImageAlt,
    });
    upsertCanonical(seo.canonicalUrl);
    upsertStructuredData(seo.structuredData);
  }, [pathname]);
}

function LogoMark({ compact = false, glow = false, locked = false }) {
  return (
    <span
      className={`logo-mark${compact ? " logo-mark--compact" : ""}${
        glow ? " logo-mark--glow" : ""
      }`}
      aria-hidden="true"
    >
      <img
        className="logo-mark__image"
        src={assets.brand.appIcon.src}
        alt=""
        width={assets.brand.appIcon.width}
        height={assets.brand.appIcon.height}
        loading="eager"
        decoding="async"
      />
      {locked ? (
        <span className="logo-mark__badge">
          <span className="logo-mark__badge-icon">{"\u{1F512}"}</span>
        </span>
      ) : null}
    </span>
  );
}

function DeviceShowcase({
  slides,
  activeIndex,
  onSelect,
  onHoverStart,
  onHoverEnd,
  onFocusEnter,
  onFocusLeave,
}) {
  const activeSlide = slides[activeIndex];

  return (
    <div
      className="device-showcase"
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      onFocusCapture={onFocusEnter}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          onFocusLeave();
        }
      }}
    >
      <div className="device-showcase__frame">
        <div className="device-showcase__screen">
          <img
            key={activeSlide.key}
            className="device-showcase__image"
            src={activeSlide.image.src}
            alt={activeSlide.alt}
            width={activeSlide.image.width}
            height={activeSlide.image.height}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            sizes="(max-width: 640px) 76vw, 344px"
          />
        </div>
      </div>

      <div className="device-showcase__copy">
        <span className="device-showcase__eyebrow">{activeSlide.eyebrow}</span>
        <h2 className="device-showcase__title">{activeSlide.title}</h2>
        <p className="device-showcase__description">
          {activeSlide.description}
        </p>
      </div>
      <div
        className="device-showcase__dots"
        aria-label="Choose an app screen"
        role="group"
      >
        {slides.map((slide, index) => (
          <button
            className={`device-showcase__dot${
              index === activeIndex ? " device-showcase__dot--active" : ""
            }`}
            key={slide.key}
            type="button"
            aria-label={`Show ${slide.label} screen`}
            aria-pressed={index === activeIndex}
            onClick={() => onSelect(index)}
          />
        ))}
      </div>
    </div>
  );
}

function FeatureCard({ card }) {
  return (
    <article className={`feature-card feature-card--${card.size}`}>
      <div className="feature-card__content">
        {card.image ? null : (
          <span className="feature-card__emoji" aria-hidden="true">
            {card.emoji}
          </span>
        )}
        <span className="feature-card__eyebrow">{card.eyebrow}</span>
        <p>{card.description}</p>
      </div>

      {card.image ? (
        <div className="feature-card__image-shell">
          <div className="feature-card__device">
            <div className="feature-card__screen">
              <img
                className="feature-card__image"
                src={card.image.src}
                alt={card.alt}
                loading="lazy"
                decoding="async"
                sizes="(max-width: 860px) 92vw, (max-width: 1040px) 44vw, 28vw"
              />
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
}

function ArchetypeTile({ archetype }) {
  return (
    <article className="archetype-tile">
      <div className="archetype-tile__image-wrap">
        <img
          className="archetype-tile__image"
          src={archetype.image.src}
          alt=""
          width={archetype.image.width}
          height={archetype.image.height}
          loading="lazy"
          decoding="async"
          sizes="(max-width: 420px) 100vw, (max-width: 640px) 48vw, (max-width: 1040px) 24vw, 12vw"
        />
      </div>
      <span className="archetype-tile__name">{archetype.name}</span>
    </article>
  );
}

function ArchetypeShowcase({
  archetypes: archetypeList,
  prefersReducedMotion,
}) {
  if (prefersReducedMotion) {
    return (
      <div className="archetype-grid" aria-label="Solstice archetypes">
        {archetypeList.map((archetype) => (
          <ArchetypeTile archetype={archetype} key={archetype.key} />
        ))}
      </div>
    );
  }

  return (
    <div className="archetype-marquee">
      <p className="sr-only">
        Available Solstice archetypes include{" "}
        {archetypeList.map((archetype) => archetype.name).join(", ")}.
      </p>

      <div className="archetype-marquee__viewport" aria-hidden="true">
        <div className="archetype-marquee__track">
          {[0, 1].map((copyIndex) => (
            <div
              className="archetype-marquee__group"
              key={`archetype-copy-${copyIndex}`}
            >
              {archetypeList.map((archetype) => (
                <ArchetypeTile
                  archetype={archetype}
                  key={`${copyIndex}-${archetype.key}`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const hasJoined = status === "success";

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (hasJoined) {
      return;
    }

    const normalizedEmail = email.trim();

    if (!isValidEmail(normalizedEmail)) {
      setStatus("error");
      setMessage("Enter a valid email address to join early access.");
      return;
    }

    try {
      setStatus("submitting");
      setMessage("");

      const response = await fetch(site.waitlistEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: normalizedEmail,
          source: "solsticediarywebsite",
        }),
      });

      if (!response.ok) {
        throw new Error("Waitlist signup failed");
      }

      setStatus("success");
      setEmail(normalizedEmail);
      setMessage(
        "You're on the list. We'll let you know when early access opens.",
      );
    } catch (error) {
      setStatus("error");
      setMessage(
        "Something went wrong while saving your email. Please try again.",
      );
    }
  };

  return (
    <form
      className="waitlist-form"
      onSubmit={handleSubmit}
      action={site.waitlistEndpoint}
      method="post"
    >
      <input type="hidden" name="source" value="solsticediarywebsite" />

      <div className="waitlist-form__row">
        <label className="waitlist-form__field">
          <span className="sr-only">Email address</span>
          <input
            className="waitlist-form__input"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="Enter your email for early access"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={status === "submitting" || hasJoined}
            required
          />
        </label>

        <button
          className="button button--primary waitlist-form__submit"
          type="submit"
          disabled={status === "submitting" || hasJoined}
        >
          {status === "submitting"
            ? "Joining..."
            : hasJoined
              ? "You're On The List"
              : "Join Early Access"}
        </button>
      </div>

      <p
        className={`waitlist-form__message${
          status === "error" ? " waitlist-form__message--error" : ""
        }${hasJoined ? " waitlist-form__message--success" : ""}`}
        aria-live="polite"
      >
        {message ||
          "We will only use your email for early-access and launch updates."}
      </p>

      <p className="waitlist-form__fineprint">
        By joining, you agree to the <a href="/terms">Terms of Service</a> and{" "}
        <a href="/privacy-policy">Privacy Policy</a>.
      </p>
    </form>
  );
}

function SiteHeader({
  headerRef,
  isMobileNav,
  isMobileMenuOpen,
  onMenuToggle,
  onNavigationSelection,
  home = false,
}) {
  const headerPanelVisible = !isMobileNav || isMobileMenuOpen;

  return (
    <header className="site-header" ref={headerRef}>
      <div className="container">
        <div className="site-header__inner">
          <div className="site-header__bar">
            <a
              className="brand"
              href={home ? "#top" : "/"}
              onClick={onNavigationSelection}
            >
              <LogoMark compact />
              <span className="brand__text">
                <strong>{site.name}</strong>
                <span>{site.tagline}</span>
              </span>
            </a>

            {isMobileNav ? (
              <button
                className="menu-toggle"
                type="button"
                aria-controls="primary-navigation"
                aria-expanded={isMobileMenuOpen}
                onClick={onMenuToggle}
              >
                <span className="menu-toggle__label">
                  {isMobileMenuOpen ? "Close" : "Menu"}
                </span>
                <span className="menu-toggle__bars" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </button>
            ) : null}
          </div>

          <div
            className={`site-header__panel${
              headerPanelVisible ? " site-header__panel--open" : ""
            }`}
            hidden={isMobileNav && !isMobileMenuOpen}
          >
            <nav
              className="site-nav"
              id="primary-navigation"
              aria-label="Primary"
            >
              {navLinks.map((link) => (
                <a
                  href={link.href}
                  key={link.href}
                  onClick={onNavigationSelection}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <a
              className="button button--primary button--compact site-header__cta"
              href={home ? "#waitlist" : "/#waitlist"}
              onClick={onNavigationSelection}
            >
              Join Early Access
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

function SiteFooter({ legalPage = false }) {
  return (
    <footer className="site-footer">
      <div className="container site-footer__inner">
        <a className="brand site-footer__brand" href={legalPage ? "/" : "#top"}>
          <LogoMark compact />
          <span className="brand__text">
            <strong>{site.name}</strong>
            <span>{site.tagline}</span>
          </span>
        </a>

        <div className="site-footer__content">
          <p className="site-footer__copy">{site.footerCopy}</p>
          <p className="site-footer__contact">
            Contact: <a href={`mailto:${site.email}`}>{site.email}</a>
          </p>
        </div>

        <div className="site-footer__links">
          {footerLinkGroups.map((group) => (
            <nav
              className="site-footer__nav"
              aria-label={group.label}
              key={group.label}
            >
              <strong>{group.label}</strong>
              {group.links.map((link) => (
                <a href={link.href} key={link.href}>
                  {link.label}
                </a>
              ))}
            </nav>
          ))}
        </div>
      </div>
    </footer>
  );
}

function LegalPage({ page }) {
  return (
    <div className="site-shell site-shell--legal">
      <div className="ambient ambient--one" aria-hidden="true" />
      <div className="ambient ambient--two" aria-hidden="true" />
      <div className="ambient ambient--three" aria-hidden="true" />

      <main className="legal-main">
        <section className="section legal-section">
          <div className="container legal-page">
            <a className="legal-page__back" href="/">
              Back to home
            </a>

            <article className="legal-page__card">
              <a className="brand legal-page__brand" href="/">
                <LogoMark compact />
                <span className="brand__text">
                  <strong>{site.name}</strong>
                  <span>{site.tagline}</span>
                </span>
              </a>

              <header className="legal-page__header">
                <span className="section-label">{page.label}</span>
                <h1>{page.title}</h1>

                <div
                  className="legal-page__meta"
                  aria-label={`${page.label} details`}
                >
                  {page.metadata.map((item) => (
                    <div className="legal-page__meta-item" key={item.label}>
                      <span className="legal-page__meta-label">
                        {item.label}
                      </span>
                      {item.href ? (
                        <a className="legal-page__meta-link" href={item.href}>
                          {item.value}
                        </a>
                      ) : (
                        <span className="legal-page__meta-value">
                          {item.value}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="legal-page__intro">
                  {page.introParagraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </header>

              <div className="legal-page__sections">
                {page.sections.map((section) => (
                  <section
                    className="legal-page__section"
                    key={section.heading}
                  >
                    <h2>{section.heading}</h2>
                    {section.paragraphs?.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                    {section.bullets?.length ? (
                      <ul className="legal-page__bullets">
                        {section.bullets.map((bullet) => (
                          <li key={bullet}>{bullet}</li>
                        ))}
                      </ul>
                    ) : null}
                  </section>
                ))}
              </div>
            </article>
          </div>
        </section>
      </main>

      <SiteFooter legalPage />
    </div>
  );
}

function getRelatedLabel(route) {
  if (contentPageByRoute[route]) {
    return contentPageByRoute[route].label;
  }

  return {
    "/terms": "Terms of Service",
    "/privacy-policy": "Privacy Policy",
    "/account-deletion": "Account and Data Deletion",
  }[route];
}

function ContentPage({ page }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobileNav = useMediaQuery("(max-width: 1040px)");
  const headerRef = useRef(null);
  const headerOffset = useElementHeight(headerRef);

  useEffect(() => {
    if (!isMobileNav) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobileNav]);

  const closeNavigation = () => setIsMobileMenuOpen(false);

  return (
    <div
      className="site-shell site-shell--content"
      style={{ "--header-offset": `${Math.max(headerOffset, 104)}px` }}
    >
      <div className="ambient ambient--one" aria-hidden="true" />
      <div className="ambient ambient--two" aria-hidden="true" />
      <div className="ambient ambient--three" aria-hidden="true" />

      <SiteHeader
        headerRef={headerRef}
        isMobileNav={isMobileNav}
        isMobileMenuOpen={isMobileMenuOpen}
        onMenuToggle={() => setIsMobileMenuOpen((open) => !open)}
        onNavigationSelection={closeNavigation}
      />

      <main>
        <article className="content-page">
          <header className="section content-page__hero">
            <div className="container content-page__hero-grid">
              <div className="content-page__intro">
                <nav className="breadcrumbs" aria-label="Breadcrumb">
                  <a href="/">Home</a>
                  <span aria-hidden="true">/</span>
                  <span aria-current="page">{page.label}</span>
                </nav>
                <span className="section-label">{page.label}</span>
                <h1>{page.title}</h1>
                <p>{page.description}</p>
                <a className="button button--primary" href="#waitlist">
                  Join Early Access
                </a>
              </div>

              <figure
                className={`content-page__visual${
                  page.visualStyle === "brand"
                    ? " content-page__visual--brand"
                    : ""
                }`}
              >
                {page.visualStyle === "brand" ? (
                  <img
                    src={page.image.src}
                    alt={page.imageAlt}
                    width={page.image.width}
                    height={page.image.height}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                ) : (
                  <div className="content-page__device">
                    <div className="content-page__screen">
                      {page.video ? (
                        <video
                          aria-label={page.video.label}
                          controls
                          muted
                          playsInline
                          preload="metadata"
                          poster={page.video.poster}
                        >
                          <source src={page.video.src} type="video/mp4" />
                          Your browser does not support the Story preview video.
                        </video>
                      ) : (
                        <img
                          src={page.image.src}
                          alt={page.imageAlt}
                          width={page.image.width}
                          height={page.image.height}
                          loading="eager"
                          fetchPriority="high"
                          decoding="async"
                        />
                      )}
                    </div>
                  </div>
                )}
              </figure>
            </div>
          </header>

          <div className="section content-page__body">
            <div className="container content-page__layout">
              <div className="content-page__sections">
                {page.sections.map((section) => (
                  <section
                    className="content-page__section"
                    key={section.heading}
                  >
                    <h2>{section.heading}</h2>
                    {section.paragraphs?.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                    {section.bullets?.length ? (
                      <ul>
                        {section.bullets.map((bullet) => (
                          <li key={bullet}>{bullet}</li>
                        ))}
                      </ul>
                    ) : null}
                  </section>
                ))}

                {page.faqs?.length ? (
                  <div className="faq-list">
                    {page.faqs.map((faq) => (
                      <section className="faq-item" key={faq.question}>
                        <h2>{faq.question}</h2>
                        <p>{faq.answer}</p>
                      </section>
                    ))}
                  </div>
                ) : null}
              </div>

              <aside
                className="related-pages"
                aria-labelledby="related-pages-title"
              >
                <span className="section-label">Keep exploring</span>
                <h2 id="related-pages-title">Related pages</h2>
                <nav aria-label="Related pages">
                  {page.relatedRoutes.map((route) => (
                    <a href={route} key={route}>
                      {getRelatedLabel(route)}
                    </a>
                  ))}
                </nav>
              </aside>
            </div>
          </div>

          <section className="section section--cta" id="waitlist">
            <div className="container">
              <div className="cta-card">
                <span className="section-label">Early access</span>
                <h2>Join the Solstice Diary waitlist.</h2>
                <p>
                  Get an email when early access opens more widely and when
                  launch details are ready.
                </p>
                <WaitlistForm />
              </div>
            </div>
          </section>
        </article>
      </main>

      <SiteFooter legalPage />
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="site-shell site-shell--legal">
      <div className="ambient ambient--one" aria-hidden="true" />
      <div className="ambient ambient--two" aria-hidden="true" />
      <div className="ambient ambient--three" aria-hidden="true" />

      <main className="legal-main">
        <section className="section legal-section">
          <div className="container not-found-page">
            <article className="not-found-card">
              <a className="brand not-found-card__brand" href="/">
                <LogoMark compact />
                <span className="brand__text">
                  <strong>{site.name}</strong>
                  <span>{site.tagline}</span>
                </span>
              </a>

              <span className="section-label">404</span>
              <h1>{notFoundPage.title}</h1>
              <p>{notFoundPage.description}</p>

              <div className="not-found-card__actions">
                <a
                  className="button button--primary"
                  href={notFoundPage.primaryLinkHref}
                >
                  {notFoundPage.primaryLinkLabel}
                </a>
                <a
                  className="button button--secondary"
                  href={notFoundPage.secondaryLinkHref}
                >
                  {notFoundPage.secondaryLinkLabel}
                </a>
              </div>
            </article>
          </div>
        </section>
      </main>

      <SiteFooter legalPage />
    </div>
  );
}

function LandingPage() {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobileNav = useMediaQuery("(max-width: 1040px)");
  const isStackedFeatures = useMediaQuery("(max-width: 860px)");
  const headerRef = useRef(null);
  const headerOffset = useElementHeight(headerRef);

  useEffect(() => {
    if (prefersReducedMotion || isHovered || hasFocusWithin) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setActiveSlideIndex(
        (currentIndex) => (currentIndex + 1) % rotatingSlides.length,
      );
    }, 4500);

    return () => window.clearTimeout(timeoutId);
  }, [activeSlideIndex, hasFocusWithin, isHovered, prefersReducedMotion]);

  useEffect(() => {
    if (!isMobileNav) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobileNav]);

  const handleNavigationSelection = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div
      className="site-shell"
      style={{ "--header-offset": `${Math.max(headerOffset, 104)}px` }}
    >
      <div className="ambient ambient--one" aria-hidden="true" />
      <div className="ambient ambient--two" aria-hidden="true" />
      <div className="ambient ambient--three" aria-hidden="true" />

      <SiteHeader
        headerRef={headerRef}
        isMobileNav={isMobileNav}
        isMobileMenuOpen={isMobileMenuOpen}
        onMenuToggle={() =>
          setIsMobileMenuOpen((currentValue) => !currentValue)
        }
        onNavigationSelection={handleNavigationSelection}
        home
      />

      <main>
        <section className="hero section" id="top">
          <div className="container hero__layout">
            <div className="hero__content">
              <span className="section-label">{homePage.sectionLabel}</span>
              <h1>{homePage.hero.title}</h1>
              <p className="hero__lead">{homePage.hero.lead}</p>

              <div className="hero__actions">
                <a
                  className="button button--primary"
                  href={homePage.hero.primaryCtaHref}
                >
                  {homePage.hero.primaryCtaLabel}
                </a>
                <a
                  className="button button--secondary"
                  href={homePage.hero.secondaryCtaHref}
                >
                  {homePage.hero.secondaryCtaLabel}
                </a>
              </div>

              <p className="hero__meta">{homePage.hero.meta}</p>
            </div>

            <div className="hero__visual">
              <DeviceShowcase
                slides={rotatingSlides}
                activeIndex={activeSlideIndex}
                onSelect={setActiveSlideIndex}
                onHoverStart={() => setIsHovered(true)}
                onHoverEnd={() => setIsHovered(false)}
                onFocusEnter={() => setHasFocusWithin(true)}
                onFocusLeave={() => setHasFocusWithin(false)}
              />
            </div>
          </div>
        </section>

        <section
          className="section section--page-links"
          aria-labelledby="page-links-title"
        >
          <div className="container page-links-section">
            <div className="section-heading">
              <span className="section-label">Product guide</span>
              <h2 id="page-links-title">
                Explore your private guided journal.
              </h2>
              <p>
                Read a clear explanation of each part of Solstice Diary before
                deciding whether early access is right for you.
              </p>
            </div>
            <div className="page-link-grid">
              {homePageLinks.map((link) => (
                <a className="page-link-card" href={link.href} key={link.href}>
                  <span>{link.label}</span>
                  <h3>{link.title}</h3>
                  <p>{link.description}</p>
                  <strong>Read the guide →</strong>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="section section--features" id="features">
          <div className="container features-section">
            <div className="section-heading features-section__intro">
              <span className="section-label">
                {homePage.featuresSection.label}
              </span>
              <h2>{homePage.featuresSection.title}</h2>
              <p>{homePage.featuresSection.description}</p>
            </div>

            <div
              className="feature-columns"
              aria-label="Solstice Diary features"
            >
              {isStackedFeatures ? (
                <div className="feature-column feature-column--stacked">
                  {stackedFeatureCards.map((card) => (
                    <FeatureCard card={card} key={card.key} />
                  ))}
                </div>
              ) : (
                featureColumns.map((column, columnIndex) => (
                  <div
                    className="feature-column"
                    key={`feature-column-${columnIndex}`}
                  >
                    {column.map((card) => (
                      <FeatureCard card={card} key={card.key} />
                    ))}
                  </div>
                ))
              )}
            </div>

            <div className="features-section__actions">
              <a
                className="button button--primary"
                href={homePage.hero.primaryCtaHref}
              >
                {homePage.hero.primaryCtaLabel}
              </a>
            </div>
          </div>
        </section>

        <section className="section section--values">
          <div className="container">
            <div className="value-grid">
              {valueCards.map((card) => (
                <article className="value-card" key={card.title}>
                  <h2>{card.title}</h2>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        <section className="section section--archetypes" id="archetypes">
          <div className="container archetypes-section">
            <div className="section-heading section-heading--compact">
              <span className="section-label section-label--compact-match">
                {homePage.archetypesSection.label}
              </span>
              <h2>{homePage.archetypesSection.title}</h2>
              <p>{homePage.archetypesSection.description}</p>
            </div>

            <ArchetypeShowcase
              archetypes={archetypes}
              prefersReducedMotion={prefersReducedMotion}
            />
          </div>
        </section>

        <section className="section section--privacy" id="privacy">
          <div className="container">
            <div className="privacy-section">
              <span className="section-label section-label--compact-match">
                {homePage.privacySection.label}
              </span>

              <div className="privacy-card">
                <div className="privacy-card__mark">
                  <LogoMark glow locked />
                </div>

                <div className="privacy-card__content">
                  <h2>{homePage.privacySection.title}</h2>
                  <p>{homePage.privacySection.description}</p>

                  <div
                    className="privacy-card__notes"
                    aria-label="Privacy highlights"
                  >
                    {privacyNotes.map((note) => (
                      <span className="privacy-card__note" key={note}>
                        {note}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className="section section--testimonials"
          id="testimonials"
          aria-labelledby="testimonials-heading"
        >
          <div className="container testimonials-section">
            <div className="section-heading section-heading--compact">
              <span className="section-label">
                {homePage.testimonialsSection.label}
              </span>
              <h2 id="testimonials-heading">
                {homePage.testimonialsSection.title}
              </h2>
              <p>{homePage.testimonialsSection.description}</p>
            </div>

            <div className="testimonials-grid">
              {testimonialCards.map((card, index) => (
                <article
                  className="testimonial-card"
                  key={`${card.name}-${index}`}
                >
                  <p className="testimonial-card__quote">{card.quote}</p>
                  <div className="testimonial-card__meta">
                    <strong>{card.name}</strong>
                    <span>{card.note}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          className="section section--availability"
          aria-labelledby="availability-heading"
        >
          <div className="container availability-section">
            <div className="availability-card">
              <span className="section-label">
                {homePage.availabilitySection.label}
              </span>
              <h2 id="availability-heading">
                {homePage.availabilitySection.title}
              </h2>
              <p>{homePage.availabilitySection.description}</p>

              <div
                className="availability-card__notes"
                aria-label="Current pricing details"
              >
                {freeAccessNotes.map((note) => (
                  <span className="availability-card__note" key={note}>
                    {note}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="section section--cta" id="waitlist">
          <div className="container">
            <div className="cta-card">
              <span className="section-label">{homePage.ctaSection.label}</span>
              <h2>{homePage.ctaSection.title}</h2>
              <p>{homePage.ctaSection.description}</p>
              <WaitlistForm />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

export default function App() {
  const pathname = usePathname();
  const legalPage = legalPages[pathname];
  const contentPage = contentPageByRoute[pathname];

  useSeoMetadata(pathname);

  if (pathname === "/") {
    return <LandingPage />;
  }

  if (legalPage) {
    return <LegalPage page={legalPage} />;
  }

  if (contentPage) {
    return <ContentPage page={contentPage} />;
  }

  if (!knownRoutes.includes(pathname)) {
    return <NotFoundPage />;
  }

  return <LandingPage />;
}
